import { OrderDirection } from '@granlupo/api-client/generated/content/zeus'
import type { InferGetServerSidePropsType } from 'next'
import { useRouter } from 'next/dist/client/router'
import NextImage from 'next/legacy/image'
import { useCallback, useEffect, useState, type ChangeEventHandler } from 'react'
import { useLoadMore } from '../../hooks/useLoadMore'
import { RespondWithNotFound, RespondWithRedirect, serverPropsHandler } from '../../libs/next/api/handlers'
import { createFetcher } from '../../libs/next/api/jsonApiHandler'
import { websiteContent } from '../../libs/next/loaders/graphql/src/contentClient'
import { CheckboxGroupComponent } from '../../src/components/form/CheckboxGroupComponent'
import { AdContent } from '../../src/components/shared/AdContent'
import { BlogPostGroup } from '../../src/components/shared/BlogPostGroup'
import { Button } from '../../src/components/shared/Button'
import { Container } from '../../src/components/shared/Container'
import { Hidden } from '../../src/components/shared/Hidden'
import { Intro } from '../../src/components/shared/Intro'
import { Layout } from '../../src/components/shared/Layout'
import { ListingIngredients } from '../../src/components/shared/ListingIngredients'
import { ProductEmptyState } from '../../src/components/shared/ProductEmptyState'
import { SuggestionModal } from '../../src/components/shared/SuggestionModal'
import { TitleEnumeration } from '../../src/components/shared/TitleEnumeration'
import { toArray } from '../../src/const/ArrayHelper'
import { FilterQuery } from '../../src/const/FilterQuery'
import { InternalLink } from '../../src/const/Links'
import { setSeoCategory } from '../../src/const/setSeo'
import { Translation, useTranslate } from '../../src/contexts/TranslationContext'
import { AdFragment } from '../../src/data/AdFragment'
import { BlogPostBannerFragment } from '../../src/data/BlogPostFragment'
import { BodyTagPlainFragment } from '../../src/data/BodyTagFragment'
import { DidYouKnowFragment } from '../../src/data/DidYouKnowFragment'
import { ItemCategoryDetailFragment } from '../../src/data/ItemCategoryFragment'
import { LayoutFragment } from '../../src/data/LayoutFragment'
import { loadCategoryItems } from '../../src/loaders/loadCategoryItems'
import style from '../../src/styles/CategoryPageDetail.module.sass'
import { roundTheCurrentTimeToMinutes } from '../../src/utils/roundTheCurrentTimeToMinutes'
import { useDidYouKnowCheck } from '../../src/utils/useDidYouKnowCheck'
import type { API } from '../api/loaders/[handler]'

type GenericPageProps = InferGetServerSidePropsType<typeof getServerSideProps>

const api = createFetcher<API>('/api/loaders/[handler]')

export default function GenericPage(props: GenericPageProps) {
	const [search, setSearch] = useState({ bodyTag: [] as string[] })
	const [isModalOpen, setIsModalOpen] = useState(false)
	const [searchValue, setSearchValue] = useState('')
	const ingredientCategory = props.data.getItemCategory
	const bodyTags = props.data.listBodyTag

	const router = useRouter()

	const categoryId = props.categoryId

	const query = useLoadMore(
		['categoryItems', categoryId, JSON.stringify(search), searchValue],
		props.listingItems,
		async (page) => api('categoryItems', { _req: { query: { id: categoryId, page } }, search, searchValue }),
	)

	const allItems = query.data?.pages.flatMap((page) => page.items)

	const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
		setSearchValue(event.currentTarget.value)
	}

	useEffect(() => {
		setSearch({
			bodyTag: toArray(router.query.bodyTag),
		})
	}, [router])

	const translate = useTranslate()

	const handleClose = useCallback(() => {
		setSearchValue('')
	}, [setSearchValue])

	const image = ingredientCategory?.image?.url && (
		<div className={style.imageWrapper}>
			<NextImage
				src={ingredientCategory?.image?.url}
				alt={translate('alternativeText.ingredientCategory')}
				title={ingredientCategory?.name}
				layout="fill"
				objectFit="cover"
			/>
		</div>
	)

	return (
		<Layout
			didYouKnow={useDidYouKnowCheck(props.data.listDidYouKnow)}
			hasBreadcrumbs
			footer={props.data.footer.getFooter}
			socials={props.data.socials.listSocial}
			seo={setSeoCategory(props.data.getItemCategory)}
			url={`${InternalLink.IngredientCategory}${props.data.getItemCategory?.id}`}
		>
			<SuggestionModal isModalOpen={isModalOpen} handleClose={() => setIsModalOpen(false)} />

			<Container size="wide" disableGutters>
				<div className={style.content}>
					<div className={style.contentLeft}>
						<Hidden breakpoint="desktopDown">{image}</Hidden>
						<AdContent ads={props.data.listAd ?? []} />
					</div>

					<Container size="narrow">
						<Hidden breakpoint="desktopUp">{image}</Hidden>
						{ingredientCategory && (
							<Intro
								title={ingredientCategory.name}
								lead={ingredientCategory.content?.blocks ?? []}
								withAd
								leftAligned
							/>
						)}

						<div className={style.filterWrapper}>
							<CheckboxGroupComponent
								title={translate('items.section.checkboxGroup.title')}
								items={bodyTags}
								isFilter
								sum={query.data?.pages[0].totalCount ?? 0}
								initialQuery={props.initialQuery}
								label="bodyTag"
							/>
						</div>
						<TitleEnumeration
							title={translate('items.section.titleEnumeration.all')}
							sum={query.data?.pages[0].totalCount ?? 0}
							buttonLabel={translate('items.section.titleEnumeration.button')}
							onChange={handleChange}
							searchedText={searchValue}
							handleClose={handleClose}
						/>
						{allItems && allItems.length > 0 ? (
							<div className={style.bannerGroup}>
								<ListingIngredients
									ingredients={query.data?.pages.flatMap((page) => page.items) ?? []}
									onNextPage={query.fetchNextPage}
									hasNextPage={Boolean(query.hasNextPage)}
									totalCount={query.data?.pages[0]?.totalCount ?? 0}
								/>
							</div>
						) : (
							<ProductEmptyState
								title={translate('ingredients.section.emptyState.title')}
								subtitle={
									<span className={style.emptyStateButton}>
										<Translation tr="ingredientCategory.emptyState.subtitle.first" />{' '}
										<Button
											type={{ variant: 'underlined' }}
											label={translate('ingredientCategory.emptyState.button')}
											onClick={() => setIsModalOpen(true)}
										/>{' '}
										<Translation tr="emptyState.subtitle.second" />
									</span>
								}
							/>
						)}
						{props.data.listBlogPost.length > 0 && (
							<div className={style.blogPostGroupWrapper}>
								<BlogPostGroup
									category={
										ingredientCategory?.declensionSixth ?? translate('ingredientCategory.blogPostGroup.category')
									}
									blogPosts={props.data.listBlogPost}
								/>
							</div>
						)}
					</Container>
				</div>
			</Container>
		</Layout>
	)
}

// export const runtime = 'experimental-edge'
export const getServerSideProps = serverPropsHandler(async (context) => {
	context.res.setHeader('Cache-Control', 's-maxage=60, stale-while-revalidate=3600')

	const content = websiteContent

	const categorySlug = String(context.query.ingredientCategory_slug)

	const data = await content('query')({
		...LayoutFragment(),
		getItemCategory: [
			{
				by: {
					slug: categorySlug,
				},
			},
			ItemCategoryDetailFragment(),
		],
		listAd: [
			{
				filter: {
					isActive: { eq: true },
					activeFrom: { lte: roundTheCurrentTimeToMinutes() },
					activeUntil: { gte: roundTheCurrentTimeToMinutes() },
					or: [{ isIngredientSectionVisible: { eq: true } }, { itemCategory: { slug: { in: [categorySlug] } } }],
				},
			},
			AdFragment(),
		],
		listBodyTag: [{ orderBy: [{ remoteId: OrderDirection.asc }] }, BodyTagPlainFragment()],
		listBlogPost: [
			{
				limit: 12,
				orderBy: [
					{
						_random: true,
					},
				],
				filter: {
					itemCategories: { slug: { in: [categorySlug] } },
					isActive: { eq: true },
					publishedAt: { lte: roundTheCurrentTimeToMinutes() },
				},
			},
			BlogPostBannerFragment(),
		],
		getRedirect: [
			{ by: { from: categorySlug || '' } },
			{
				from: true,
				to: true,
				itemCategory: [{}, { slug: true }],
			},
		],
		listDidYouKnow: [
			{
				limit: 40,
				orderBy: [
					{
						_random: true,
					},
				],
			},
			DidYouKnowFragment(),
		],
	})

	if (!data.getItemCategory?.id) {
		if (data.getRedirect) {
			const redirect = {
				permanent: true,
				destination: String(data.getRedirect.itemCategory?.slug) || String(data.getRedirect.to),
			}
			throw new RespondWithRedirect(redirect)
		} else {
			throw new RespondWithNotFound('404 Error: Page not found')
		}
	}

	return {
		props: {
			data,
			categoryId: data?.getItemCategory?.id,
			initialQuery: FilterQuery.parse(context.query),
			listingItems: await loadCategoryItems(data?.getItemCategory?.id, 1),
		},
	}
})
